import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OrderAttachments from "@/components/OrderAttachments";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const SIZES = ["small", "medium", "large"];

const toValue = (a) =>
  a && a.line1 ? { ...a, label: [a.business, a.line1, a.city, a.postcode].filter(Boolean).join(", ") } : null;

const cleanAddr = (a) => ({
  business: a.business || "", line1: a.line1 || "", line2: a.line2 || "",
  city: a.city || "", postcode: a.postcode || "", lat: a.lat, lng: a.lng,
  contact_name: a.contact_name || "", contact_phone: a.contact_phone || "",
  customer_reference: a.customer_reference || "",
});

export const EditOrderDialog = ({ order, onSaved, triggerClassName }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [items, setItems] = useState([]);
  const [instructions, setInstructions] = useState("");
  const isFlorist = order.customer_type === "florist";
  const isPaid = order.payment?.status === "paid";
  const { user } = useAuth();
  const canOverride = user?.role === "admin" && order.payment?.status === "on_account" && !order.payment?.billed;
  const [priceTotal, setPriceTotal] = useState("");

  const init = () => {
    setPriceTotal(order.price?.total != null ? String(order.price.total) : "");
    setPickup(toValue(order.pickup));
    setDropoff(toValue(order.dropoff));
    setItems((order.items?.length ? order.items : [{ size: "small", quantity: 1, weight_kg: 1 }]).map((i) => ({ ...i })));
    setInstructions(order.delivery_instructions || "");
  };

  const setItem = (idx, field, val) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { delivery_instructions: instructions };
      if (pickup?.lat != null) payload.pickup = cleanAddr(pickup);
      if (dropoff?.lat != null) payload.dropoff = cleanAddr(dropoff);
      if (!isFlorist) {
        payload.items = items.map((i) => ({
          size: i.size, quantity: Math.max(parseInt(i.quantity, 10) || 1, 1),
          weight_kg: parseFloat(i.weight_kg) || 1, parcel_description: i.parcel_description || "",
        }));
      }
      if (canOverride && priceTotal !== "" && (order.price.manual_override || Math.abs(parseFloat(priceTotal) - order.price.total) >= 0.01)) {
        payload.price_total = parseFloat(priceTotal);
      }
      const { data } = await api.patch(`/orders/${order.id}`, payload);
      const diff = data.price_change || 0;
      if (data.adjustment?.type === "increase") {
        toast.success(`Order updated — new total $${data.order.price.total.toFixed(2)}. Customer texted to pay the $${data.adjustment.amount.toFixed(2)} difference.`);
      } else if (data.adjustment?.type === "decrease") {
        toast.success(`Order updated — new total $${data.order.price.total.toFixed(2)}. Customer texted to choose a refund or store credit for $${data.adjustment.amount.toFixed(2)}.`);
      } else if (Math.abs(diff) >= 0.01) {
        toast.success(`Order updated — new total $${data.order.price.total.toFixed(2)}`);
      } else {
        toast.success("Order updated");
      }
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update the order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) init(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`edit-order-button-${order.tracking_number}`}
          className={triggerClassName || "gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10"}>
          <Pencil className="h-3.5 w-3.5" /> Edit order
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {order.tracking_number}</DialogTitle>
          <DialogDescription>
            {isPaid
              ? "This order is already paid — if the price changes, the customer will be texted to pay the difference or choose a refund/credit."
              : "Changes reprice the order before payment."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <AddressAutocomplete label="Pickup address" value={pickup} onSelect={setPickup} testid="edit-pickup-address" />
          <AddressAutocomplete label="Drop-off address" value={dropoff} onSelect={setDropoff} testid="edit-dropoff-address" />
          {!isFlorist && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Packages</Label>
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`edit-item-row-${i}`}>
                  <Select value={it.size} onValueChange={(v) => setItem(i, "size", v)}>
                    <SelectTrigger className="w-28" data-testid={`edit-item-size-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)}
                    className="w-16" data-testid={`edit-item-qty-${i}`} />
                  <Input type="number" min="0.1" step="0.1" value={it.weight_kg} onChange={(e) => setItem(i, "weight_kg", e.target.value)}
                    className="w-20" data-testid={`edit-item-weight-${i}`} />
                  <span className="text-xs text-muted-foreground">kg</span>
                  {items.length > 1 && (
                    <button type="button" onClick={() => setItems((prev) => prev.filter((_, x) => x !== i))} data-testid={`edit-item-remove-${i}`}
                      className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              ))}
              <Button type="button" variant="ghost" size="sm" data-testid="edit-add-item"
                onClick={() => setItems((prev) => [...prev, { size: "small", quantity: 1, weight_kg: 1 }])} className="gap-1 text-kilted-green">
                <Plus className="h-3.5 w-3.5" /> Add package
              </Button>
            </div>
          )}
          {canOverride && (
            <div>
              <Label className="text-sm font-semibold">Price for this job (inc GST)</Label>
              <Input type="number" min="0" step="0.01" value={priceTotal} onChange={(e) => setPriceTotal(e.target.value)} className="mt-1 w-40" data-testid="edit-price-override" />
              <p className="mt-1 text-xs text-muted-foreground">Leave as-is to keep the calculated price. Changing addresses or packages recalculates it unless you set a price here.</p>
            </div>
          )}
          <div>
            <Label className="text-sm font-semibold">Delivery instructions</Label>
            <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2}
              className="mt-1" data-testid="edit-instructions" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Attached files</Label>
            <div className="mt-1.5">
              <OrderAttachments order={order} onChanged={onSaved} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-order-save" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditOrderDialog;
